import { useState, useCallback } from "react";

interface ModelTier {
  name: string;
  label: string;
  inputCost: number;  // $ per 1M tokens
  outputCost: number; // $ per 1M tokens
  benchmark: string;
  color: string;
  bgColor: string;
  borderColor: string;
  badgeColor: string;
  bestFor: string;
}

const MODELS: ModelTier[] = [
  {
    name: "Sol",
    label: "GPT-5.6 Sol",
    inputCost: 5.0,
    outputCost: 30.0,
    benchmark: "88.8%",
    color: "text-amber-400",
    bgColor: "bg-amber-950/40",
    borderColor: "border-amber-500/40",
    badgeColor: "bg-amber-500/20 text-amber-300",
    bestFor: "Strategy, agentic workflows, board presentations",
  },
  {
    name: "Terra",
    label: "GPT-5.6 Terra",
    inputCost: 2.5,
    outputCost: 15.0,
    benchmark: "84.3%",
    color: "text-blue-400",
    bgColor: "bg-blue-950/40",
    borderColor: "border-blue-500/40",
    badgeColor: "bg-blue-500/20 text-blue-300",
    bestFor: "Content ops, campaign analysis, GTM workflows",
  },
  {
    name: "Luna",
    label: "GPT-5.6 Luna",
    inputCost: 1.0,
    outputCost: 6.0,
    benchmark: "82.5%",
    color: "text-cyan-400",
    bgColor: "bg-cyan-950/40",
    borderColor: "border-cyan-500/40",
    badgeColor: "bg-cyan-500/20 text-cyan-300",
    bestFor: "Classification, extraction, metadata, localisation",
  },
];

function formatCost(value: number): string {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  if (value >= 1) return `$${value.toFixed(2)}`;
  return `$${value.toFixed(4)}`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return n.toString();
}

export default function TokenCostCalculator() {
  const [monthlyRequests, setMonthlyRequests] = useState<number>(100_000);
  const [inputTokens, setInputTokens] = useState<number>(2_000);
  const [outputTokens, setOutputTokens] = useState<number>(500);
  const [cacheHitRate, setCacheHitRate] = useState<number>(0);

  const handleInput = useCallback(
    (setter: (v: number) => void, raw: string, min: number, max: number) => {
      const val = parseInt(raw.replace(/,/g, ""), 10);
      if (!isNaN(val)) setter(Math.max(min, Math.min(max, val)));
    },
    []
  );

  const computeCost = (model: ModelTier) => {
    const totalInputTokens = monthlyRequests * inputTokens;
    const totalOutputTokens = monthlyRequests * outputTokens;

    // Apply cache: cached reads get 90% discount, cache writes cost 1.25x
    const cachedInputTokens = totalInputTokens * (cacheHitRate / 100);
    const uncachedInputTokens = totalInputTokens * (1 - cacheHitRate / 100);
    const cacheWriteTokens = cachedInputTokens * 0.1; // first write per 30-min window

    const inputCost =
      (uncachedInputTokens / 1_000_000) * model.inputCost +
      (cachedInputTokens / 1_000_000) * model.inputCost * 0.1 +
      (cacheWriteTokens / 1_000_000) * model.inputCost * 1.25;

    const outputCost = (totalOutputTokens / 1_000_000) * model.outputCost;

    return {
      total: inputCost + outputCost,
      inputCost,
      outputCost,
      totalInputTokens,
      totalOutputTokens,
    };
  };

  const costs = MODELS.map((m) => ({ model: m, ...computeCost(m) }));
  const maxCost = Math.max(...costs.map((c) => c.total));
  const solCost = costs[0].total;
  const lunaCost = costs[2].total;
  const solVsLunaSaving = solCost > 0 ? ((solCost - lunaCost) / solCost) * 100 : 0;

  return (
    <div className="my-10 rounded-2xl border border-slate-700 bg-slate-900 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-700 bg-slate-800/60">
        <h3 className="text-lg font-bold text-white mb-1">
          GPT-5.6 Token Cost Calculator
        </h3>
        <p className="text-sm text-slate-400">
          Estimate your monthly API spend across Sol, Terra, and Luna based on your actual workload.
        </p>
      </div>

      {/* Inputs */}
      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-5 border-b border-slate-700">
        <div>
          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Monthly API Requests
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={1000}
              max={10_000_000}
              step={1000}
              value={monthlyRequests}
              onChange={(e) => setMonthlyRequests(Number(e.target.value))}
              className="flex-1 accent-amber-500 h-2 cursor-pointer"
            />
            <span className="text-white font-mono text-sm w-16 text-right">
              {formatNumber(monthlyRequests)}
            </span>
          </div>
          <div className="flex justify-between text-xs text-slate-600 mt-1">
            <span>1k</span><span>10M</span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Input Tokens per Request
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={100}
              max={50_000}
              step={100}
              value={inputTokens}
              onChange={(e) => setInputTokens(Number(e.target.value))}
              className="flex-1 accent-blue-500 h-2 cursor-pointer"
            />
            <span className="text-white font-mono text-sm w-16 text-right">
              {formatNumber(inputTokens)}
            </span>
          </div>
          <div className="flex justify-between text-xs text-slate-600 mt-1">
            <span>100</span><span>50k</span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Output Tokens per Request
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={50}
              max={10_000}
              step={50}
              value={outputTokens}
              onChange={(e) => setOutputTokens(Number(e.target.value))}
              className="flex-1 accent-cyan-500 h-2 cursor-pointer"
            />
            <span className="text-white font-mono text-sm w-16 text-right">
              {formatNumber(outputTokens)}
            </span>
          </div>
          <div className="flex justify-between text-xs text-slate-600 mt-1">
            <span>50</span><span>10k</span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Prompt Cache Hit Rate{" "}
            <span className="text-slate-500 normal-case font-normal">(90% read discount)</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={cacheHitRate}
              onChange={(e) => setCacheHitRate(Number(e.target.value))}
              className="flex-1 accent-emerald-500 h-2 cursor-pointer"
            />
            <span className="text-white font-mono text-sm w-16 text-right">
              {cacheHitRate}%
            </span>
          </div>
          <div className="flex justify-between text-xs text-slate-600 mt-1">
            <span>0% (no cache)</span><span>100%</span>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {costs.map(({ model, total, inputCost, outputCost }) => {
          const barWidth = maxCost > 0 ? (total / maxCost) * 100 : 0;
          const isCheapest = total === Math.min(...costs.map((c) => c.total));
          return (
            <div
              key={model.name}
              className={`rounded-xl border p-4 ${model.bgColor} ${model.borderColor} relative`}
            >
              {isCheapest && (
                <span className="absolute top-3 right-3 text-xs font-semibold bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                  Cheapest
                </span>
              )}
              <div className={`text-xs font-bold uppercase tracking-wider mb-1 ${model.color}`}>
                {model.label}
              </div>
              <div className={`text-2xl font-bold text-white mb-1`}>
                {formatCost(total)}
                <span className="text-sm font-normal text-slate-400">/mo</span>
              </div>
              {/* Bar */}
              <div className="h-1.5 bg-slate-700 rounded-full mb-3 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    model.name === "Sol"
                      ? "bg-amber-500"
                      : model.name === "Terra"
                      ? "bg-blue-500"
                      : "bg-cyan-500"
                  }`}
                  style={{ width: `${barWidth}%` }}
                />
              </div>
              <div className="space-y-1 text-xs text-slate-400">
                <div className="flex justify-between">
                  <span>Input cost</span>
                  <span className="text-slate-300 font-mono">{formatCost(inputCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Output cost</span>
                  <span className="text-slate-300 font-mono">{formatCost(outputCost)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-slate-700/60">
                  <span>TerminalBench</span>
                  <span className={`font-semibold ${model.color}`}>{model.benchmark}</span>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500 leading-relaxed">{model.bestFor}</p>
            </div>
          );
        })}
      </div>

      {/* Insight banner */}
      {solVsLunaSaving > 0 && (
        <div className="mx-6 mb-5 rounded-lg bg-slate-800 border border-slate-700 px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-400">
            At this volume, choosing{" "}
            <span className="text-cyan-400 font-semibold">Luna</span> over{" "}
            <span className="text-amber-400 font-semibold">Sol</span> saves{" "}
            <span className="text-white font-bold">
              {formatCost(solCost - lunaCost)}/month
            </span>{" "}
            ({solVsLunaSaving.toFixed(0)}% reduction). Use Sol only when reasoning depth materially changes the outcome.
          </span>
        </div>
      )}

      {/* Footer note */}
      <div className="px-6 pb-5">
        <p className="text-xs text-slate-600">
          Estimates based on published OpenAI API pricing (July 2026): Sol $5/$30, Terra $2.50/$15, Luna $1/$6 per 1M input/output tokens. Cache reads apply a 90% discount; cache writes bill at 1.25× the uncached input rate. Actual costs may vary with batch discounts, tool calls, and reasoning effort settings. Always test against your own representative workloads.
        </p>
      </div>
    </div>
  );
}
